import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { urlValidator } from '@app/core/utils';
import { AppConfigApi } from '@app/features/app-content/models/app-config.model';
import { AppConfigurationState } from '@app/features/app-content/states/app-configuration.state';
import { StorePrivacySetting } from '@app/features/app-content/states/privacy.actions';
import { UiModule } from '@app/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import {
  Observable,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  filter,
} from 'rxjs';

@Component({
  selector: 'rk-privacy-section',
  standalone: true,
  templateUrl: './privacy-section.component.html',
  styleUrl: './privacy-section.component.scss',
  imports: [UiModule, TranslatePipe, ReactiveFormsModule, AsyncPipe],
})
export class PrivacySectionComponent implements OnInit, OnDestroy {
  @Select(AppConfigurationState.currentAppConfig)
  currentAppConfig$: Observable<AppConfigApi>;
  @Select(AppConfigurationState.cmpIsAvailable)
  cmpIsAvailable$: Observable<boolean>;

  cmpOptionEnabled = false;
  didomiApiKey = new FormControl(null);
  didomiNoticeId = new FormControl(null);
  termsOfServiceUrl = new FormControl(null, [urlValidator]);
  privacyPolicyUrl = new FormControl(null, [urlValidator]);

  private readonly subscriptions = new Subscription();

  constructor(private readonly store: Store) {}

  ngOnInit(): void {
    // set initial values from app config
    this.subscriptions.add(
      this.currentAppConfig$
        .pipe(filter(config => !!config))
        .subscribe(config => {
          this.cmpOptionEnabled = config.cmpOption;
          this.didomiApiKey.setValue(config.didomiApiKey, { emitEvent: false });
          this.didomiNoticeId.setValue(config.didomiNoticeId, { emitEvent: false });
          this.termsOfServiceUrl.setValue(config.termsOfServiceUrl, {
            emitEvent: false,
          });
          this.privacyPolicyUrl.setValue(config.privacyPolicyUrl, {
            emitEvent: false,
          });

          // sync fetched values to store so every save includes the full set
          this.store.dispatch(new StorePrivacySetting('cmpOption', config.cmpOption));
          this.store.dispatch(
            new StorePrivacySetting('didomiApiKey', config.didomiApiKey),
          );
          this.store.dispatch(
            new StorePrivacySetting('didomiNoticeId', config.didomiNoticeId),
          );
          this.store.dispatch(
            new StorePrivacySetting('termsOfServiceUrl', config.termsOfServiceUrl),
          );
          this.store.dispatch(
            new StorePrivacySetting('privacyPolicyUrl', config.privacyPolicyUrl),
          );
        }),
    );

    // react to didomi api key changes
    this.subscriptions.add(
      this.didomiApiKey.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(value => {
          this.store.dispatch(new StorePrivacySetting('didomiApiKey', value));
        }),
    );

    // react to didomi notice id changes
    this.subscriptions.add(
      this.didomiNoticeId.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(value => {
          this.store.dispatch(new StorePrivacySetting('didomiNoticeId', value));
        }),
    );

    // react to terms of service url changes (only dispatch when valid)
    this.subscriptions.add(
      this.termsOfServiceUrl.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(value => {
          if (this.termsOfServiceUrl.valid) {
            this.store.dispatch(new StorePrivacySetting('termsOfServiceUrl', value));
          }
        }),
    );

    // react to privacy policy url changes (only dispatch when valid)
    this.subscriptions.add(
      this.privacyPolicyUrl.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(value => {
          if (this.privacyPolicyUrl.valid) {
            this.store.dispatch(new StorePrivacySetting('privacyPolicyUrl', value));
          }
        }),
    );
  }

  onCmpOptionToggle(enabled: boolean): void {
    this.cmpOptionEnabled = enabled;
    this.store.dispatch(new StorePrivacySetting('cmpOption', enabled));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
